import { z } from "zod/v4";

/**
 * What the audit knows about a store's Shopify account, in our words rather
 * than Shopify's.
 *
 * These are the endpoint's contract, so they live here rather than beside the
 * code that builds them: `/v2/opportunity-audit/shopify` returns them and the
 * AI reads them, and a type either side declared for itself would be two
 * answers to "what is a shipping zone".
 *
 * Every `null` here means something specific and none of them mean zero. The
 * comments say which, because that distinction has already produced merchant-
 * facing defects more than once in this feature.
 */

export const MoneySchema = z.object({
  amount: z.number(),
  currencyCode: z.string(),
});

/** A condition that gates a shipping method, e.g. "order value >= 75". */
export const ShippingConditionSchema = z.object({
  field: z.string(),
  operator: z.string(),
  /** Set when the condition is on order value. */
  money: MoneySchema.nullable(),
  /** Set when the condition is on weight. */
  weight: z.object({ value: z.number(), unit: z.string() }).nullable(),
});

export const ShippingMethodSchema = z.object({
  id: z.string(),
  name: z.string(),
  active: z.boolean(),
  /** The rate charged. `0` is a free method; null means carrier-calculated. */
  price: MoneySchema.nullable(),
  conditions: z.array(ShippingConditionSchema),
  /** The carrier app that prices a carrier-calculated method, else null. */
  carrierServiceName: z.string().nullable(),
});

export const ShippingZoneSchema = z.object({
  id: z.string(),
  name: z.string(),
  countryCodes: z.array(z.string()),
  /**
   * The same countries with the regions the zone names. No regions is the
   * whole country; a US zone for Alaska and Hawaii lists those two.
   */
  countries: z.array(
    z.object({
      code: z.string(),
      /** Shopify's own name, which its sales reports use too. */
      name: z.string(),
      provinces: z.array(z.object({ code: z.string(), name: z.string() })),
    })
  ),
  /**
   * True when this is Shopify's "Rest of world" zone. Its countries cannot be
   * enumerated, so a count built from `countryCodes` would understate reach —
   * and without this flag such a zone is indistinguishable from one whose
   * countries we failed to read.
   */
  shipsRestOfWorld: z.boolean(),
  profileId: z.string(),
  locationGroupId: z.string(),
  profileName: z.string(),
  isDefaultProfile: z.boolean(),
  /** False for a profile an app created, such as a dropshipper's or a feed's. */
  profileIsMerchantOwned: z.boolean(),
  /**
   * The merchant's own methods. Our shipping test's rates are removed, since
   * they are a variant of the test and not the merchant's setup.
   */
  methods: z.array(ShippingMethodSchema),
  /** How many of our own test rates were removed from `methods`. */
  ourMethodCount: z.number(),
  /**
   * The store has more profiles, zones or rates than the read takes, so some
   * are missing. The same on every zone of one read: nothing may be said of
   * every zone.
   */
  readCutOff: z.boolean(),
});

export const StoreThemeSchema = z.object({
  /**
   * The NUMERIC id, not the gid. A theme redirect is built with
   * `{key: "preview_theme_id", value: <numeric>}`, and a gid cannot be turned
   * back into one at that end without knowing where it came from.
   */
  id: z.string(),
  name: z.string(),
  /** MAIN is the live theme; the rest are alternatives worth testing against. */
  role: z.string(),
  processing: z.boolean(),
  /** ISO. Separates a live draft from a 2022 backup. */
  updatedAt: z.string(),
  /**
   * ISO. Only meaningful next to `updatedAt`: a theme whose whole lifetime is
   * seconds long was duplicated and never touched again.
   */
  createdAt: z.string(),
});

/** filename → text content, for the files that would carry our script. */
export const ThemeFilesSchema = z.object({
  detect: z.record(z.string(), z.string()),
});

/** The live theme, which is the only one the audit reads on its own. */
export const LiveThemeSchema = StoreThemeSchema.extend({
  /**
   * Whether this theme serves *this org's* Intelligems script.
   *
   * The verdict, not the evidence. The files it is read from are large and the
   * decision is one the backend is better placed to make: it holds the same
   * regexes `script-status` uses, so the audit and org health cannot disagree
   * about whether a theme is instrumented.
   */
  detected: z.boolean(),
});

/** Who the store is, for the briefing header. Not its numbers. */
export const StoreFactsSchema = z.object({
  name: z.string().nullable(),
  currencyCode: z.string().nullable(),
  /** Published and unpublished alike — Shopify counts the catalogue, not the storefront. */
  productCount: z.number().nullable(),
  /** Active and published: what a shopper can be shown. */
  activeProductCount: z.number().nullable(),
  /**
   * Where the shop itself is, from its address in Shopify. Optional because
   * stored facts from before it have none.
   */
  countryCode: z.string().nullable().optional(),
  /**
   * Whether the storefront sits behind a password. Optional because stored
   * facts from before it have none.
   */
  passwordProtected: z.boolean().nullable().optional(),
  /**
   * The shop's own description, as search engines show it. Optional because
   * stored facts from before it have none.
   */
  description: z.string().nullable().optional(),
  /** The name on the Shopify account, sometimes a company. Optional as above. */
  shopOwnerName: z.string().nullable().optional(),
  /** Every currency a buyer can pay in. Optional as above. */
  presentmentCurrencies: z.array(z.string()).optional(),
  /** Where shoppers reach the storefront, like `https://shop.example.com`. Optional as above. */
  storefrontUrl: z.string().nullable().optional(),
});

/**
 * An automatic discount running on the store, as Shopify describes it. Our own
 * app's are left out. Another app's discount carries only its title.
 */
export const LiveDiscountSchema = z.object({
  type: z.enum(["basic", "bxgy", "freeShipping", "app"]),
  title: z.string(),
  /** Shopify's own sentence, such as "10% off entire order". None on an app's. */
  summary: z.string().nullable(),
  /**
   * The share taken off, 0 to 1: of the order or item on a basic discount, of
   * the item you get on buy X get Y. Null on a basic discount means an amount.
   */
  percentage: z.number().nullable(),
  /** The item count a basic discount needs before it applies. */
  minimumQuantity: z.number().nullable(),
  /** Buy X get Y only: whether the shopper qualifies by spending an amount. */
  buysAmount: z.boolean().nullable(),
  /**
   * Buy X get Y only: the products bought and given. Null when a side names a
   * collection or every item, so which products it covers is not known.
   */
  buysProductIds: z.array(z.string()).nullable(),
  getsProductIds: z.array(z.string()).nullable(),
});

/** What a merchant can name on their store, as a search can find it. */
export const StoreSearchKindSchema = z.enum([
  "product",
  "collection",
  "page",
  "article",
  "blog",
]);

/** One thing a store search found, in the form a name resolves to. */
export const StoreSearchMatchSchema = z.object({
  kind: StoreSearchKindSchema,
  /** The Shopify gid. */
  id: z.string(),
  handle: z.string(),
  title: z.string(),
});

/** A product an upsell can offer, in the shape the rule stores. */
export const UpsellProductSchema = z.object({
  productId: z.string(),
  title: z.string(),
  handle: z.string(),
  /** Shopify's own: `ACTIVE`, `DRAFT` or `ARCHIVED`. */
  status: z.string(),
  /** Sold only on subscription, so a one-time Add cannot buy it. */
  requiresSellingPlan: z.boolean(),
  /** Has a subscription plan at all, so its repeat orders may be renewals. */
  hasSellingPlan: z.boolean(),
  isGiftCard: z.boolean(),
  /** Null when unpublished from the online store, or on a password-protected store. */
  onlineStoreUrl: z.string().nullable(),
  image: z.string().nullable(),
  variants: z.array(
    z.object({
      variantId: z.string(),
      title: z.string(),
      price: z.number(),
      compareAtPrice: z.number().nullable(),
      availableForSale: z.boolean(),
      /** The cost entered in Shopify. Null when none is entered; zero reads as none. */
      unitCost: z.number().nullable(),
      options: z.array(z.object({ name: z.string(), value: z.string() })),
    })
  ),
});

/**
 * A collection with its first members in best-selling order, the order a
 * post-purchase offer resolves it in. Members are the first 20 Shopify lists,
 * drafts and unpublished ones included, so the reader can drop those and still
 * see the first ten a buyer could be offered.
 */
export const CollectionMembersSchema = z.object({
  collectionId: z.string(),
  handle: z.string(),
  title: z.string(),
  /** Every product in it, whatever its status. Null when Shopify does not say. */
  productsCount: z.number().nullable(),
  members: z.array(
    z.object({
      productId: z.string(),
      active: z.boolean(),
      published: z.boolean(),
    })
  ),
});

export const CollectionListingSchema = z.object({
  collections: z.array(CollectionMembersSchema),
  /** True when the store has more collections than one read takes. */
  readCutOff: z.boolean(),
});

export const PricedVariantSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  price: z.number(),
  /**
   * The list price this one is marked down from, where there is one.
   *
   * Not decoration: on 3,155,055 control variants of real price tests, 33.3%
   * carry a compare-at above the live price, at a median markdown of 28.6%. So
   * a third of the products a price test covers sit at a promotional price, and
   * proposing a rise on one is partly a decision to stop discounting. Null when
   * the product is at list.
   */
  compareAtPrice: z.number().nullable(),
  /**
   * List-price unit cost. Null when no cost is entered; zero on the wire reads
   * as null.
   *
   * Every other cost source in the audit derives from `gross_profit /
   * net_sales`, and `net_sales` is after discounts — so on a product selling
   * part of its units on promotion those sources overstate cost and understate
   * margin. This one carries no discount at all.
   */
  unitCost: z.number().nullable(),
  currencyCode: z.string().nullable(),
});

export const PricedProductSchema = z.object({
  id: z.string(),
  handle: z.string(),
  title: z.string(),
  /** Shopify builds it from other products. Price tests cannot cover those. */
  isBundle: z.boolean(),
  /** Sold on a selling plan, so it needs its own entitlement. */
  isSubscription: z.boolean(),
  variants: z.array(PricedVariantSchema),
});

/**
 * A product's identity and whether a price test can cover it, keyed by the id a
 * revenue ranking already holds.
 *
 * The ranking comes from ShopifyQL, which knows what each product earns and
 * nothing about how it is sold, so the two flags that decide buildability are
 * read from the Admin API against the same ids.
 */
export const ProductIdentitySchema = z.object({
  handle: z.string(),
  title: z.string(),
  isBundle: z.boolean(),
  isSubscription: z.boolean(),
  vendor: z.string().nullable(),
  /** Shopify's taxonomy path, like `Gift Cards`. */
  category: z.string().nullable(),
  isGiftCard: z.boolean(),
  /** Null when the product has no page on the online store. */
  onlineStoreUrl: z.string().nullable(),
});

/**
 * A product listed without any reference to sales — the audit's only
 * revenue-free route to "which products", and so the only one that works on a
 * store that has never sold anything.
 */
export const CatalogProductSchema = z.object({
  id: z.string(),
  handle: z.string(),
  title: z.string(),
  price: z.number().nullable(),
  currencyCode: z.string().nullable(),
  totalInventory: z.number().nullable(),
  isBundle: z.boolean(),
  isSubscription: z.boolean(),
  vendor: z.string().nullable(),
  /** Shopify's taxonomy path, like `Gift Cards`. */
  category: z.string().nullable(),
  isGiftCard: z.boolean(),
  /** Null when the product has no page on the online store. */
  onlineStoreUrl: z.string().nullable(),
});

/**
 * How often a product's buyers come back for it, over online store orders in
 * the last year. Subscription renewals are not online store orders, so they
 * are not in it.
 */
/** Net sales and orders through one sales channel, Shopify's name for it. */
export const ChannelSalesSchema = z.object({
  channel: z.string(),
  netSales: z.number(),
  orders: z.number(),
});

export const RepeatPurchaseSchema = z.object({
  productId: z.string(),
  orders: z.number(),
  customers: z.number(),
});

export const ProductPerformanceSchema = z.object({
  productId: z.string(),
  title: z.string(),
  netSales: z.number(),
  orders: z.number(),
  /** Null when the store has not entered product costs. */
  grossProfit: z.number().nullable(),
  /**
   * The part of `netSales` Shopify actually costed. Null when unknown; zero
   * means no unit carried a cost.
   *
   * This is the denominator `margin` needs: gross profit is only earned on the
   * costed part, so dividing by the whole of `netSales` dilutes the margin
   * toward zero in exact proportion to the coverage gap.
   */
  costedNetSales: z.number().nullable(),
  /** Share of the window's total net sales, 0 to 1. */
  revenueShare: z.number(),
  /**
   * Gross profit over the revenue that carried a cost, or null without costs.
   *
   * Measured on 12,081 product rows from 286 live stores: 2.5% are costed on
   * only part of their revenue, and on those the naive `grossProfit /
   * netSales` reading understated margin by a median 4.3 points.
   */
  margin: z.number().nullable(),
});

export const CountryOrdersSchema = z.object({
  /** Shopify's own country name, e.g. "United States". */
  country: z.string(),
  /** Shopify's own region name, e.g. "Hawaii". Null when the order had none. */
  region: z.string().nullable(),
  orders: z.number(),
  netSales: z.number(),
});

/** Human sessions that landed on one path from one kind of traffic. */
export const AuditLandingPageSchema = z.object({
  /** As Shopify reports it: a locale prefix, a trailing slash and all. */
  path: z.string(),
  /**
   * Shopify's own grouping: Homepage, Product, Collection, Custom Page and so
   * on. It is Shopify's vocabulary, not ours, and anything mapping it to our
   * own names has to handle a value it does not recognise.
   */
  type: z.string(),
  /** Shopify's `traffic_type`: paid, direct, organic, unknown or unattributed. */
  trafficType: z.string(),
  sessions: z.number(),
});

export const AuditLandingPagesSchema = z.object({
  rows: z.array(AuditLandingPageSchema),
  /** Every human session in the window, the rows past the limit included. */
  totalSessions: z.number(),
  /** Whether the read stopped at its limit, so the least visited pages are missing. */
  truncated: z.boolean(),
});

/** Sales from orders whose session landed on one path from one kind of traffic. */
export const AuditLandingPageSalesSchema = z.object({
  path: z.string(),
  trafficType: z.string(),
  netSales: z.number(),
  orders: z.number(),
});

export const AuditLandingSalesSchema = z.object({
  /** Orders with no landing path, like subscription renewals, are not here. */
  rows: z.array(AuditLandingPageSalesSchema),
  /** Whether the read stopped at its limit, so the least earning pages are missing. */
  truncated: z.boolean(),
});

/** The kinds of page our pixel reads separately, each capped at its busiest ten. */
export const AuditPixelPageTypeSchema = z.enum([
  "PDP",
  "Collection",
  "Content",
  "Blog",
]);

/** One landing page as the Intelligems pixel counted it. */
export const AuditPixelLandingPageSchema = z.object({
  /** `/` for the homepage. Otherwise as the pixel recorded it. */
  path: z.string(),
  /** The pixel's kind of page, when the row came from a read of one kind. */
  type: AuditPixelPageTypeSchema.nullable(),
  sessions: z.number(),
  orders: z.number(),
  netRevenue: z.number(),
});

export const AuditPixelLandingPagesSchema = z.object({
  rows: z.array(AuditPixelLandingPageSchema),
  /** Every session in the window, the pages no read named included. */
  totalSessions: z.number(),
  /** Every order from those sessions. */
  totalOrders: z.number(),
  /** Whether a read folded pages past its ten into one row, so some pages are missing. */
  truncated: z.boolean(),
});

/**
 * One funnel stage, as Shopify counts it, beside what normal looks like.
 *
 * **Both sides are Shopify's own numbers.** The store's rate is exactly what
 * its Shopify admin reports, so a merchant can check it; the band is measured
 * the same way across other stores. Benchmarking a Shopify rate against our
 * pixel's medians means converting between two instruments that disagree per
 * store, which supports a verdict but not a magnitude.
 *
 * `rate` is null when the stage has no denominator — a store with no cart
 * additions has no cart-to-checkout rate, which is an absence rather than a
 * zero.
 */
export const AuditFunnelStageSchema = z.object({
  stage: z.enum(["addToCart", "reachedCheckout", "purchase", "conversion"]),
  rate: z.number().nullable(),
  /** Absent when the population has no published band for this stage. */
  benchmark: z
    .object({ p25: z.number(), median: z.number(), p75: z.number() })
    .nullable(),
});

export const AuditFunnelSchema = z.object({
  /** The days, back from today, that the counts and rates cover. */
  windowDays: z.number().int().positive(),
  /**
   * Raw counts, kept beside the rates because a rate cannot say which half
   * moved — and because the session count sizes how long a test would take.
   */
  counts: z.object({
    sessions: z.number().nullable(),
    cartAdditions: z.number().nullable(),
    reachedCheckout: z.number().nullable(),
    reachedAndCompletedCheckout: z.number().nullable(),
    completedCheckout: z.number().nullable(),
  }),
  stages: z.array(AuditFunnelStageSchema),
  /**
   * Which population the bands describe: this store's industry, or every
   * store we measured. They do not mean the same thing to a merchant.
   */
  population: z.enum(["industry", "all"]),
  /** Null on the `all` population, where naming an industry would mislead. */
  industry: z.string().nullable(),
  /**
   * When the bands were measured and on what. They are rebuilt by hand rather
   * than on a schedule, so anything showing them can say how old they are.
   */
  benchmarkMeasuredAt: z.string(),
  benchmarkStores: z.number(),
});

export type AuditFunnelType = z.infer<typeof AuditFunnelSchema>;

export const OrderCountSchema = z.object({
  count: z.number(),
  /**
   * Shopify's own word for how exact the count is. Carried rather than reduced
   * to a boolean so `EXACT` stays distinguishable from `UNSPECIFIED`: the cap
   * (`AT_LEAST`, hit by 29% of a 118-store sample) is definitive upward, but an
   * unspecified count is not definitive in either direction.
   */
  precision: z.enum(["EXACT", "AT_LEAST", "UNSPECIFIED"]),
  /**
   * The days the count covers: the window asked for, or fewer when the store's
   * first order falls inside it. Optional because counts cached before it
   * have none.
   */
  windowDays: z.number().optional(),
});

/** One line of a sampled order, as Shopify returned it. */
export const OrderLineRecordSchema = z.object({
  productId: z.string().nullable(),
  variantId: z.string().nullable(),
  quantity: z.number(),
  /** The line's total after its discounts. Zero for a line given away. */
  total: z.number(),
});

/**
 * One sampled order, kept whole so a node can ask its own question of it: by
 * product, by destination, by date.
 */
export const OrderRecordSchema = z.object({
  placedAt: z.string(),
  total: z.number(),
  subtotal: z.number(),
  /** Paid units, counted as `quantities` counts them. */
  units: z.number(),
  /** Country and state codes only. Null when the order has no shipping address. */
  destination: z
    .object({ country: z.string().nullable(), province: z.string().nullable() })
    .nullable(),
  /** Its first 20 lines. */
  lines: z.array(OrderLineRecordSchema),
});

export const OrderValueSampleSchema = z.object({
  /** Order totals: merchandise, less discounts, plus shipping and tax. */
  values: z.array(z.number()),
  /**
   * Merchandise subtotals after discounts, index-aligned with `values`.
   *
   * A separate quantity because Shopify evaluates a delivery profile's
   * free-shipping condition on the subtotal. Asking what share of order
   * *totals* clear a *subtotal* threshold overstates it in one direction:
   * totals carry shipping and tax the condition never sees.
   */
  subtotals: z.array(z.number()),
  /**
   * Units per order, index-aligned with `values`.
   *
   * The only place the audit can see the units-per-order *distribution*: the
   * pixel rollup carries the mean and nothing else.
   */
  quantities: z.array(z.number()),
  /** The shop's own currency, so a caller never has to infer one from a total. */
  currencyCode: z.string(),
  /**
   * True when the read hit its page cap, so this is a sample rather than the
   * whole window. Anything reading a share off these arrays has to say so.
   */
  truncated: z.boolean(),
  /**
   * When the oldest and newest sampled orders were placed, ISO. A share read
   * off the sample names this range, because a capped read covers a median 13
   * days of the 90 asked for. Null on an empty sample.
   */
  oldest: z.string().nullable(),
  newest: z.string().nullable(),
  /**
   * Units sold per variant id, over the orders read. Optional because samples
   * cached before it have none.
   */
  unitsByVariant: z.record(z.string(), z.number()).optional(),
  /**
   * Per order, the products the buyer paid for, from its first 20 lines. A
   * line added after payment, by an upsell, is in it too. Optional because
   * samples cached before it have none.
   */
  baskets: z.array(z.array(z.string())).optional(),
  /** The sampled orders themselves, in the order they were read. */
  orders: z.array(OrderRecordSchema),
});

/**
 * What a section is to the page it sits on.
 *
 * `content` is the page's own, `buyBox` the product page's section that adds
 * to the cart, `generated` a section the theme or an app fills, and
 * `furniture` what a content test is not built on, like a popup or a divider.
 */
export const PageSectionRoleSchema = z.enum([
  "content",
  "buyBox",
  "generated",
  "furniture",
]);

/**
 * What a shopper can act on in a section, read from the served body. What
 * decides whether a test could change how products are sorted or what is
 * selected there: sorting needs a list, a default needs a choice.
 */
export const SectionHoldsSchema = z.object({
  /** Distinct products it links to. */
  productLinks: z.number(),
  addToCartForm: z.boolean(),
  /** Cart lines, a form to the cart or checkout, or an empty cart's message. */
  cartForm: z.boolean(),
  /** A variant, quantity or selling-plan choice. A country or language picker is not one. */
  optionPicker: z.boolean(),
  /**
   * A list of products the served body does not show yet: a recommendation or
   * search app's mount, a search form, or products named only in its scripts.
   */
  productList: z.boolean(),
});

/** One section of a storefront page, identified. */
export const AnalysedSectionSchema = z.object({
  /** The section's key in its template, group or layout, e.g. `main` or `image_banner_Xp8TqL`. */
  key: z.string(),
  /** What kind of section it is, as the theme names it, e.g. `image-banner`. */
  type: z.string(),
  /** The section's own wrapper on the rendered page, which is where a change is built. */
  selector: z.string(),
  /** Whether it belongs to this page or renders on every page, like the header. */
  scope: z.enum(["page", "sitewide"]),
  role: PageSectionRoleSchema,
  /** Position among its scope's sections that are not furniture, from 1. Null for furniture. */
  position: z.number().nullable(),
  /**
   * How well this section was read, which is what a reader weighs a finding
   * by. **Per section, because one page's sections are not read alike**: a
   * section holding no text is `declared` on a page whose neighbours are
   * `markup`, and a pass that looks closely at the pages that matter, or at
   * the crops above the fold, leaves the rest of a page behind.
   *
   * - `declared`: it exists and is named. The theme's files say so, or its
   *   wrapper held nothing to read. Nothing is known about what it carries.
   * - `markup`: the body the storefront served. Text the server sent, blind
   *   to anything a script draws.
   * - `rendered`: the DOM once scripts have run. Text a visitor can read,
   *   widgets included, blind to words inside an image. **Not produced yet.**
   * - `visitor`: the picture. Images, prominence, and where the fold really
   *   falls. **Not produced yet.**
   */
  read: z.enum(["declared", "markup", "rendered", "visitor"]),
  /**
   * Characters of text it shows, **in the DOM this read saw**. Null when it
   * was never reached, so zero and unknown stay apart.
   *
   * What zero means moves with `read`. At `markup` it is a gap: a script may
   * have drawn words the served body never carried. At `rendered` it is a
   * fact about the page — there are no words here, and anything it says is in
   * a picture.
   */
  textLength: z.number().nullable(),
  /**
   * The text it shows in that same DOM, one line per block and cut short.
   * Null when its markup was never read. Defaults to null for analyses cached
   * before it was kept.
   */
  text: z.string().nullable().default(null),
  /** Null when its markup was never read, as from a theme's files. */
  holds: SectionHoldsSchema.nullable(),
  /**
   * Whether something may fill it after the page loads, so a section served
   * empty is not known to be empty: a script or an app block in its served
   * markup, or on the cart, what a shopper adds. Null from a theme's files.
   */
  fillsLater: z.boolean().nullable(),
});

/**
 * One storefront page, analysed: every section identified, and what the whole
 * page visibly carries.
 *
 * Read from the page as it is served, or from the theme's template files when
 * the page could not be read or has no Shopify sections. Null when neither
 * answers.
 */
export const PageAnalysisSchema = z.object({
  /** The page as read, after redirects. */
  url: z.string(),
  /**
   * How the page was read.
   *
   * `html` is the body the storefront served, `rendered` a real browser render
   * that also sees what a script draws, and `template` the theme's files, which
   * name a page's sections and say nothing about what they carry.
   *
   * **How the page was reached, not how well any section was read.** A reader
   * weighing a finding wants the section's own `read`, which this bounds but
   * does not decide.
   */
  source: z.enum(["html", "rendered", "template"]),
  /**
   * What builds the page. `theme` for a Shopify theme. `pageBuilder` for a
   * page a theme serves whose own content sits outside its sections, like a
   * Replo page, which has no section of its own to name. `custom` for a page
   * served without a theme, like a headless storefront, which has no sections
   * to name and whose theme files are not what visitors see.
   */
  builtBy: z.enum(["theme", "pageBuilder", "custom"]),
  /**
   * The page's language, from `<html lang>`, e.g. `en-US`. What the page
   * shows is partly read from its text in English, so a page in another
   * language can show more than was found. Null from a template, or when the
   * page does not say.
   */
  language: z.string().nullable(),
  /**
   * What a page builder's page says outside its sections, one line per block
   * and cut short as a section's text is. Null on any other page, and on
   * analyses cached before it was kept.
   */
  text: z.string().nullable().default(null),
  sections: z.array(AnalysedSectionSchema),
});

export type PageSectionRoleType = z.infer<typeof PageSectionRoleSchema>;
export type SectionHoldsType = z.infer<typeof SectionHoldsSchema>;
export type AnalysedSectionType = z.infer<typeof AnalysedSectionSchema>;
export type PageAnalysisType = z.infer<typeof PageAnalysisSchema>;
export type MoneyType = z.infer<typeof MoneySchema>;
export type ShippingZoneType = z.infer<typeof ShippingZoneSchema>;
export type ShippingMethodType = z.infer<typeof ShippingMethodSchema>;
export type ShippingConditionType = z.infer<typeof ShippingConditionSchema>;
export type StoreThemeType = z.infer<typeof StoreThemeSchema>;
export type LiveThemeType = z.infer<typeof LiveThemeSchema>;
export type ThemeFilesType = z.infer<typeof ThemeFilesSchema>;
export type StoreFactsType = z.infer<typeof StoreFactsSchema>;
export type LiveDiscountType = z.infer<typeof LiveDiscountSchema>;
export type StoreSearchKindType = z.infer<typeof StoreSearchKindSchema>;
export type StoreSearchMatchType = z.infer<typeof StoreSearchMatchSchema>;
export type UpsellProductType = z.infer<typeof UpsellProductSchema>;
export type PricedVariantType = z.infer<typeof PricedVariantSchema>;
export type PricedProductType = z.infer<typeof PricedProductSchema>;
export type ProductIdentityType = z.infer<typeof ProductIdentitySchema>;
export type OrderRecordType = z.infer<typeof OrderRecordSchema>;
export type CatalogProductType = z.infer<typeof CatalogProductSchema>;
export type ProductPerformanceType = z.infer<typeof ProductPerformanceSchema>;
export type CountryOrdersType = z.infer<typeof CountryOrdersSchema>;
export type AuditLandingPageType = z.infer<typeof AuditLandingPageSchema>;
export type AuditLandingPagesType = z.infer<typeof AuditLandingPagesSchema>;
export type AuditLandingPageSalesType = z.infer<
  typeof AuditLandingPageSalesSchema
>;
export type AuditLandingSalesType = z.infer<typeof AuditLandingSalesSchema>;
export type AuditPixelPageType = z.infer<typeof AuditPixelPageTypeSchema>;
export type AuditPixelLandingPagesType = z.infer<
  typeof AuditPixelLandingPagesSchema
>;
export type OrderCountType = z.infer<typeof OrderCountSchema>;
export type OrderValueSampleType = z.infer<typeof OrderValueSampleSchema>;
export type CollectionMembersType = z.infer<typeof CollectionMembersSchema>;
export type CollectionListingType = z.infer<typeof CollectionListingSchema>;
export type RepeatPurchaseType = z.infer<typeof RepeatPurchaseSchema>;
export type ChannelSalesType = z.infer<typeof ChannelSalesSchema>;
